import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Link,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { submitFeedback } from './api/feedback';

const MAX_LENGTH = 500;
const MotionBox = motion(Box);

export default function HelpFeedback() {
  const [message, setMessage] = useState('');
  const [okayToContact, setOkayToContact] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const messageRef = useRef(null);
  const toast = useToast();

  const charsUsed = message.length;
  const trimmedLength = message.trim().length;
  const introColor = useColorModeValue('gray.700', 'whiteAlpha.800');
  const helperColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const counterColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const helpTextColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const successLinkColor = useColorModeValue('teal.600', 'teal.200');

  useEffect(() => {
    messageRef.current?.focus();
  }, []);

  const handleMessageChange = (event) => {
    const nextValue = event.target.value.slice(0, MAX_LENGTH);
    setMessage(nextValue);
    setFieldError('');
    setServerError('');
    if (success) setSuccess(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFieldError('');
    setServerError('');

    const trimmed = message.trim();
    if (!trimmed) {
      setFieldError('Please tell us what is happening.');
      return;
    }

    setSubmitting(true);
    try {
      await submitFeedback({ message: trimmed, okayToContact });
      setSuccess(true);
      setMessage('');
      setOkayToContact(false);
      toast({
        title: 'Feedback sent',
        description: 'Thanks for sharing that with us.',
        status: 'success',
        duration: 3500,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err) {
      setServerError(err.message || 'Unable to send feedback right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MotionBox
      maxW="720px"
      mx="auto"
      p={4}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2 }}
    >
      <Heading size="lg" mb={2}>
        Help &amp; Feedback
      </Heading>
      <Text mb={6} color={introColor}>
        If something is confusing, broken, or you have an idea, let me know here.
      </Text>

      <Stack spacing={4}>
        {success && (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <AlertDescription display="flex" flexDirection="column" gap={2}>
              <span>Thanks for the feedback — it’s been recorded.</span>
              <Link as={RouterLink} to="/dashboard" color={successLinkColor}>
                Back to Ekkos
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {serverError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        )}

        <Box as="form" onSubmit={handleSubmit}>
          <Stack spacing={5}>
            <FormControl isInvalid={Boolean(fieldError)}>
              <FormLabel>Tell us what’s happening</FormLabel>
              <Textarea
                value={message}
                placeholder="Describe what you were trying to do, and what actually happened."
                onChange={handleMessageChange}
                maxLength={MAX_LENGTH}
                rows={6}
                resize="vertical"
                ref={messageRef}
                id="feedback-message"
                autoFocus
              />
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Text fontSize="sm" color={helperColor}>
                  Describe what you were trying to do, and what actually happened.
                </Text>
                <Text fontSize="sm" color={counterColor}>
                  {charsUsed}/{MAX_LENGTH}
                </Text>
              </Box>
              <FormErrorMessage>{fieldError}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={okayToContact}
                onChange={(e) => {
                  setOkayToContact(e.target.checked);
                  setServerError('');
                }}
              >
                It&apos;s okay to contact me about this
              </Checkbox>
              <Text fontSize="sm" color={helpTextColor} mt={2}>
                We’ll use the email on your account if we need more detail.
              </Text>
            </FormControl>

            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
              <Button
                type="submit"
                colorScheme="blue"
                borderRadius="full"
                isLoading={submitting}
                isDisabled={!trimmedLength || submitting}
              >
                Submit
              </Button>
              <Button as={RouterLink} to="/dashboard" variant="ghost">
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </MotionBox>
  );
}
